/**
 * Advisor API Service
 */

import { BaseApiService } from "@metlas/shared";
import type {
  StudentInfo,
  ApiResponse,
  AskQuestionResponse,
  AdminDashboardResponse,
  StudentFeedbackRequest,
  AdminFeedbackResponse,
  UpdateFeedbackActionRequest,
} from "../types/api";
import type {
  QAManagementResponse,
  QASyncResponse,
  QAQuestion,
  QAAuditLogsResponse,
  QAIncludedFilter,
} from "../types/qa-management";

/** Wire events streamed by the Metlas Intelligence admin chat (SSE). */
export type MetlasStreamEvent =
  | { type: "session"; session_id: string; rotated: boolean }
  | { type: "text"; delta: string }
  | { type: "tool_start"; name: string; input: unknown }
  | { type: "tool_result"; ok: boolean; rowCount?: number; error?: string }
  | {
      type: "done";
      reply: string;
      toolCalls: Array<{ name: string; input: unknown; ok: boolean }>;
      usage: {
        inputTokens: number;
        outputTokens: number;
        cacheCreationInputTokens: number;
        cacheReadInputTokens: number;
      };
    }
  | { type: "error"; message: string };

export interface MetlasChatPayload {
  message: string;
  start_date: string;
  end_date: string;
  program_code?: string;
  history?: Array<{ role: "user" | "assistant"; content: string }>;
  session_id?: string;
}

class AdvisorApiService extends BaseApiService {
  constructor() {
    super("Advisor");
  }

  /** Base URL the axios client resolved (used for the raw fetch SSE call). */
  private get baseUrl(): string {
    return this.client.defaults.baseURL || "";
  }

  /**
   * Stream one Metlas Intelligence turn over SSE. Uses `fetch` (not axios) so we
   * can read the response body incrementally; cookie auth rides along via
   * `credentials: 'include'`. `onEvent` fires per parsed frame; resolves when the
   * stream ends. Pass an AbortSignal to cancel mid-stream.
   */
  async streamMetlasIntelligence(
    payload: MetlasChatPayload,
    onEvent: (event: MetlasStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const res = await fetch(
      `${this.baseUrl}/api/v1/cucs-advising/admin/metlas-intelligence/chat`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal,
      },
    );

    if (!res.ok || !res.body) {
      let message = `Request failed (${res.status})`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) message = body.error;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line.
      let sep = buffer.indexOf("\n\n");
      while (sep !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        for (const line of frame.split("\n")) {
          if (!line.startsWith("data:")) continue; // skip ': keepalive' comments
          const json = line.slice(5).trim();
          if (!json) continue;
          try {
            onEvent(JSON.parse(json) as MetlasStreamEvent);
          } catch {
            /* ignore malformed frame */
          }
        }
        sep = buffer.indexOf("\n\n");
      }
    }
  }

  async getMetlasHistory(sessionId: string): Promise<
    ApiResponse<{
      session_id: string | null;
      turns: Array<{ role: "user" | "assistant"; content: string }>;
    }>
  > {
    return this.get(
      `/api/v1/cucs-advising/admin/metlas-intelligence/history?session_id=${encodeURIComponent(sessionId)}`,
    );
  }

  async askQuestion(
    message: string,
    studentInfo?: StudentInfo,
    history?: Array<{ role: "user" | "assistant"; content: string }>,
    aiGenerationEnabled?: boolean,
  ): Promise<ApiResponse<AskQuestionResponse>> {
    return this.post<ApiResponse<AskQuestionResponse>>(
      "/api/v1/cucs-advising/chat",
      {
        message,
        student_info: studentInfo,
        history,
        ai_generation_enabled: aiGenerationEnabled,
      },
    );
  }

  async askQuestionDirect(
    message: string,
    studentInfo?: StudentInfo,
    history?: Array<{ role: "user" | "assistant"; content: string }>,
  ): Promise<ApiResponse<AskQuestionResponse>> {
    return this.post<ApiResponse<AskQuestionResponse>>(
      "/api/v1/cucs-advising/chat/direct",
      {
        message,
        student_info: studentInfo,
        history,
      },
    );
  }

  async rateQuality(
    id: string,
    isRelevant?: boolean,
    researcherNotes?: string,
    flagged?: boolean,
  ): Promise<ApiResponse> {
    return this.post<ApiResponse>("/api/v1/cucs-advising/rate-quality", {
      id,
      is_relevant: isRelevant,
      researcher_notes: researcherNotes,
      flagged,
    });
  }

  async getAdminData(
    limit: number = 100,
  ): Promise<ApiResponse<AdminDashboardResponse>> {
    return this.get<ApiResponse<AdminDashboardResponse>>(
      `/api/v1/cucs-advising/admin?limit=${limit}`,
    );
  }

  async submitFeedback(
    feedback: StudentFeedbackRequest,
  ): Promise<ApiResponse<{ id: string }>> {
    return this.post<ApiResponse<{ id: string }>>(
      "/api/v1/cucs-advising/feedback",
      feedback,
    );
  }

  async getAdminFeedbackData(
    limit: number = 100,
  ): Promise<ApiResponse<AdminFeedbackResponse>> {
    return this.get<ApiResponse<AdminFeedbackResponse>>(
      `/api/v1/cucs-advising/admin/feedback?limit=${limit}`,
    );
  }

  async updateFeedbackAction(
    data: UpdateFeedbackActionRequest,
  ): Promise<ApiResponse> {
    return this.post<ApiResponse>(
      "/api/v1/cucs-advising/admin/rate-feedback",
      data,
    );
  }

  async sendAgentReply(data: {
    feedback_id: string;
    agent_reply: string;
    agent_name: string;
  }): Promise<ApiResponse<{ reply_id: string }>> {
    return this.post<ApiResponse<{ reply_id: string }>>(
      "/api/v1/cucs-advising/admin/send-reply",
      data,
    );
  }

  async getAgentReplies(
    feedbackId: string,
  ): Promise<
    ApiResponse<{
      replies: Record<string, { reply: string; agent_name: string }>;
    }>
  > {
    return this.get<
      ApiResponse<{
        replies: Record<string, { reply: string; agent_name: string }>;
      }>
    >(`/api/v1/cucs-advising/admin/replies?feedback_id=${feedbackId}`);
  }

  async getProgramProfiles(): Promise<
    ApiResponse<{
      profiles: { student_type: string; school: string; program: string }[];
    }>
  > {
    return this.get<
      ApiResponse<{
        profiles: { student_type: string; school: string; program: string }[];
      }>
    >("/api/v1/cucs-advising/program_profiles");
  }

  async getQAManagementData(
    limit: number = 5,
    offset: number = 0,
    search: string = "",
    included: QAIncludedFilter = "all",
  ): Promise<ApiResponse<QAManagementResponse>> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (search.trim()) params.append("search", search.trim());
    if (included !== "all") params.append("included", included);
    return this.get<ApiResponse<QAManagementResponse>>(
      `/api/v1/cucs-advising/admin/qa_management?${params.toString()}`,
    );
  }

  async syncQAToS3(): Promise<ApiResponse<QASyncResponse>> {
    return this.post<ApiResponse<QASyncResponse>>(
      "/api/v1/cucs-advising/admin/qa_management/sync",
      {},
      { timeout: 300000 },
    );
  }

  async getProgramCodes(): Promise<
    ApiResponse<{ program_codes: string[]; program_codes_in_use?: string[] }>
  > {
    return this.get<
      ApiResponse<{ program_codes: string[]; program_codes_in_use?: string[] }>
    >("/api/v1/cucs-advising/admin/qa_management/program_codes");
  }

  async getVisaRequirements(): Promise<
    ApiResponse<{ visa_requirements: string[] }>
  > {
    return this.get<ApiResponse<{ visa_requirements: string[] }>>(
      "/api/v1/cucs-advising/admin/qa_management/visa_requirements",
    );
  }

  async getStatuses(): Promise<ApiResponse<{ statuses: string[] }>> {
    return this.get<ApiResponse<{ statuses: string[] }>>(
      "/api/v1/cucs-advising/admin/qa_management/statuses",
    );
  }

  async getQAQuestion(questionId: string): Promise<ApiResponse<QAQuestion>> {
    return this.get<ApiResponse<QAQuestion>>(
      `/api/v1/cucs-advising/admin/qa_management/${questionId}`,
    );
  }

  async updateQAQuestion(
    questionId: string,
    data: {
      question: string;
      answer: string;
      program_codes: string[];
      statuses: string[];
      visa_requirements: string[];
    },
  ): Promise<ApiResponse<QAQuestion>> {
    return this.put<ApiResponse<QAQuestion>>(
      `/api/v1/cucs-advising/admin/qa_management/${questionId}`,
      data,
    );
  }

  /**
   * Include (true) or exclude (false) a Q&A from chatbot retrieval and the
   * KB export. Excluded Q&As stay in the database and can be re-included.
   */
  async setQAIncluded(
    questionId: string,
    isIncluded: boolean,
  ): Promise<ApiResponse<QAQuestion & { variants_updated: number }>> {
    return this.patch<ApiResponse<QAQuestion & { variants_updated: number }>>(
      `/api/v1/cucs-advising/admin/qa_management/${questionId}/included`,
      { is_included: isIncluded },
    );
  }

  async deleteQAQuestion(questionId: string): Promise<ApiResponse> {
    return this.delete<ApiResponse>(
      `/api/v1/cucs-advising/admin/qa_management/${questionId}`,
    );
  }

  async createQAQuestion(data: {
    question: string;
    answer: string;
    program_codes: string[];
    statuses: string[];
    visa_requirements: string[];
  }): Promise<ApiResponse<QAQuestion>> {
    return this.post<ApiResponse<QAQuestion>>(
      "/api/v1/cucs-advising/admin/qa_management",
      data,
    );
  }

  async swapQAProgramCode(
    currentCode: string,
    updatedCode: string,
  ): Promise<
    ApiResponse<{
      current_code: string;
      updated_code: string;
      updated_count: number;
      mapping_updated: boolean;
    }>
  > {
    return this.post<
      ApiResponse<{
        current_code: string;
        updated_code: string;
        updated_count: number;
        mapping_updated: boolean;
      }>
    >("/api/v1/cucs-advising/admin/qa_management/program_code_swap", {
      current_code: currentCode,
      updated_code: updatedCode,
    });
  }

  async getNoMatchTemplate(): Promise<ApiResponse<{ value: string }>> {
    return this.get<ApiResponse<{ value: string }>>(
      "/api/v1/cucs-advising/admin/qa_management/config/no_match_message",
    );
  }

  async updateNoMatchTemplate(
    value: string,
  ): Promise<ApiResponse<{ value: string }>> {
    return this.put<ApiResponse<{ value: string }>>(
      "/api/v1/cucs-advising/admin/qa_management/config/no_match_message",
      { value },
    );
  }

  async getQAAuditLogs(
    limit: number = 20,
    offset: number = 0,
  ): Promise<ApiResponse<QAAuditLogsResponse>> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    return this.get<ApiResponse<QAAuditLogsResponse>>(
      `/api/v1/cucs-advising/admin/qa_management/audit_logs?${params.toString()}`,
    );
  }
}

export const advisorApiService = new AdvisorApiService();
